import { useMemo, useRef, useState, type ComponentType } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CheckCircle2,
  ChevronRight,
  ChevronUp,
  HelpCircle,
  type LucideIcon,
  Pencil,
  Trash2,
} from "lucide-react";
import { RequireRole } from "@/lib/auth";
import { BackButton } from "@/components/BackButton";
import { Badge, lotStatusBadge, lotTypeBadge } from "@/components/Badge";
import { DropdownField, FormField, NumberField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import { SectionHeader } from "@/components/SectionHeader";
import {
  calcCostPerCard,
  calcGlobalCostPerCard,
  calcLandedCost,
  useApp,
  type Card,
  type CardGame,
  type LotStatus,
  type SortingBin,
} from "@/context/AppContext";
import {
  RARITY_DISPLAY_ORDER,
  rarityCountsForLot,
} from "@/features/lotRarityCounts";
import {
  configForGame,
  GAME_PICKER_OPTIONS,
  type SortingCategoryDef,
} from "@/features/tcgGameConfig";

const GAME_LABELS: Record<CardGame, string> = {
  Pokemon: "Pokémon",
  Magic: "Magic: The Gathering",
  "Yu-Gi-Oh": "Yu-Gi-Oh!",
  "One Piece": "One Piece",
  Other: "Other",
};

/** Icon for a category id within a game's config, with a safe fallback. */
function iconForCategory(
  categories: SortingCategoryDef[],
  id: string,
): LucideIcon {
  return categories.find((c) => c.id === id)?.icon ?? HelpCircle;
}

export const Route = createFileRoute("/lots/$id")({
  component: LotDetailPage,
});

// Sorting-bin taxonomy + card→bin categorization are now game-defined; see
// features/tcgGameConfig.ts. The lot's `game` selects the config used below.

const LOT_STATUSES: LotStatus[] = [
  "Unsorted",
  "Sorting",
  "Sorted",
  "Listed",
  "Partially Sold",
  "Closed",
];

function LotDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { lots, cards, updateLot, deleteLot } = useApp();

  const lot = useMemo(() => lots.find((l) => l.id === id), [lots, id]);

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [expandBins, setExpandBins] = useState(false);
  const [editingBin, setEditingBin] = useState<string | null>(null);
  const [binCount, setBinCount] = useState("");
  const [binCountError, setBinCountError] = useState<string | null>(null);
  const [binLocation, setBinLocation] = useState("");
  const [binNotes, setBinNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmDeleteBin, setConfirmDeleteBin] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  const [showBackfillSheet, setShowBackfillSheet] = useState(false);
  const [editSeller, setEditSeller] = useState("");
  const [editPurchaseLocation, setEditPurchaseLocation] = useState("");
  const [editReceiptNumber, setEditReceiptNumber] = useState("");

  if (!lot) {
    return (
      <div className="flex flex-1 items-center justify-center text-foreground">
        Lot not found
      </div>
    );
  }

  const landed = calcLandedCost(lot);
  const costPerCard = calcCostPerCard(lot);
  const portfolioAvg = calcGlobalCostPerCard(lots);
  const showPortfolioAvg = portfolioAvg > 0 && lots.length > 1;
  const totalSorted = lot.sortingBins.reduce((s, b) => s + b.count, 0);

  const lotCards = cards.filter((c) => c.sourceLotId === lot.id);
  const totalInventoried = lotCards.reduce((s, c) => s + c.quantity, 0);
  const isFullyInventoried =
    lot.estimatedCards > 0 && totalInventoried >= lot.estimatedCards;
  const lotInvCost = lotCards.reduce((s, c) => s + c.costBasis * c.quantity, 0);
  const lotInvValue = lotCards.reduce(
    (s, c) =>
      s +
      ((c.expectedSalePrice > 0 ? c.expectedSalePrice : c.marketPrice) || 0) *
        c.quantity,
    0,
  );
  const lotProjProfit = lotInvValue - lotInvCost;
  const lotProjROI = lotInvCost > 0 ? (lotProjProfit / lotInvCost) * 100 : null;

  // Game config drives the sorting-bin taxonomy + categorization. Falls back
  // to Pokémon for legacy lots (no `game`).
  const gameConfig = configForGame(lot.game);
  const categoryIds = gameConfig.sortingCategories.map((c) => c.id);

  const derivedBins = new Map<string, number>();
  for (const c of lotCards) {
    const cat = gameConfig.categorize(c);
    derivedBins.set(cat, (derivedBins.get(cat) ?? 0) + c.quantity);
  }
  const derivedBinRows = categoryIds
    .filter((cat) => (derivedBins.get(cat) ?? 0) > 0)
    .map((cat) => ({ cat, count: derivedBins.get(cat) ?? 0 }));

  const rarityCounts = rarityCountsForLot(lot, lotCards);
  const rarityRows = RARITY_DISPLAY_ORDER.filter(
    (r) => (rarityCounts.byRarity[r] ?? 0) > 0,
  );

  const handleSaveBin = () => {
    if (!editingBin) return;
    const parsed = parseInt(binCount, 10);
    const isValid = /^\d+$/.test(binCount.trim()) && parsed >= 0;
    if (!isValid) {
      setBinCountError("Count must be a non-negative whole number.");
      fieldRefs.current[editingBin]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    setBinCountError(null);
    const count = parsed;
    const existing = lot.sortingBins.find((b) => b.category === editingBin);
    let bins: SortingBin[];
    if (existing) {
      bins = lot.sortingBins.map((b) =>
        b.category === editingBin
          ? { ...b, count, location: binLocation, notes: binNotes }
          : b,
      );
    } else {
      bins = [
        ...lot.sortingBins,
        {
          category: editingBin,
          count,
          location: binLocation,
          notes: binNotes,
          costBasis: count * costPerCard,
        },
      ];
    }
    updateLot({ ...lot, sortingBins: bins });
    setEditingBin(null);
    setBinCount("");
    setBinLocation("");
    setBinNotes("");
  };

  const handleStatusChange = (s: string) => {
    updateLot({ ...lot, status: s as LotStatus });
  };

  const handleGameChange = (g: string) => {
    // Switching game re-derives the sorting taxonomy. Existing bins keep their
    // old category strings (shown with the fallback icon) until re-sorted.
    updateLot({ ...lot, game: g as CardGame });
  };

  const openDetailsEditor = () => {
    setEditSeller(lot.seller ?? "");
    setEditPurchaseLocation(lot.purchaseLocation ?? "");
    setEditReceiptNumber(lot.receiptNumber ?? "");
    setEditingDetails(true);
  };

  const handleSaveDetails = () => {
    updateLot({
      ...lot,
      seller: editSeller.trim(),
      purchaseLocation: editPurchaseLocation.trim() || undefined,
      receiptNumber: editReceiptNumber.trim() || undefined,
    });
    setEditingDetails(false);
  };

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    deleteLot(lot.id);
    navigate({ to: "/lots" });
  };

  const openBinEditor = (cat: string) => {
    const existing = lot.sortingBins.find((b) => b.category === cat);
    setEditingBin(cat);
    setBinCount(existing ? String(existing.count) : "");
    setBinCountError(null);
    setBinLocation(existing?.location ?? "");
    setBinNotes(existing?.notes ?? "");
  };

  const handleDeleteBin = (cat: string) => {
    if (confirmDeleteBin !== cat) {
      setConfirmDeleteBin(cat);
      return;
    }
    const bins = lot.sortingBins.filter((b) => b.category !== cat);
    updateLot({ ...lot, sortingBins: bins });
    setConfirmDeleteBin(null);
    if (editingBin === cat) {
      setEditingBin(null);
      setBinCount("");
      setBinLocation("");
      setBinNotes("");
    }
  };

  const handleApplyBackfill = (mode: "merge" | "replace") => {
    if (mode === "merge") {
      const updatedBins = [...lot.sortingBins];
      derivedBins.forEach((count, cat) => {
        const idx = updatedBins.findIndex((b) => b.category === cat);
        if (idx !== -1 && updatedBins[idx].count > 0) return;
        const costBasisForBin = count * costPerCard;
        if (idx !== -1) {
          updatedBins[idx] = {
            ...updatedBins[idx],
            count,
            costBasis: costBasisForBin,
          };
        } else {
          updatedBins.push({
            category: cat,
            count,
            location: "",
            notes: "",
            costBasis: costBasisForBin,
          });
        }
      });
      updateLot({ ...lot, sortingBins: updatedBins });
    } else {
      const updatedBins = lot.sortingBins.map((bin) => {
        const count = derivedBins.get(bin.category) ?? 0;
        return { ...bin, count, costBasis: count * costPerCard };
      });
      derivedBins.forEach((count, cat) => {
        if (!updatedBins.find((b) => b.category === cat)) {
          updatedBins.push({
            category: cat,
            count,
            location: "",
            notes: "",
            costBasis: count * costPerCard,
          });
        }
      });
      updateLot({ ...lot, sortingBins: updatedBins });
    }
    setShowBackfillSheet(false);
  };

  return (
    <>
      {showBackfillSheet && (
        <BackfillSheet
          rows={derivedBinRows}
          categories={gameConfig.sortingCategories}
          onClose={() => setShowBackfillSheet(false)}
          onApply={handleApplyBackfill}
        />
      )}

      <div className="space-y-4 pb-12">
        <BackButton fallback={{ to: "/lots" }} variant="wide" />

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-1">
            <div className="text-[10px] font-medium tracking-wide text-muted-foreground">
              {lot.id}
            </div>
            {editingDetails ? (
              <div className="mt-1 space-y-1 rounded-md border border-border bg-card p-3">
                <FormField
                  label="Seller"
                  value={editSeller}
                  onChangeText={setEditSeller}
                  placeholder="Seller name or username"
                />
                <FormField
                  label="Purchase Location"
                  value={editPurchaseLocation}
                  onChangeText={setEditPurchaseLocation}
                  placeholder="eBay, TCGPlayer, local card shop, etc."
                />
                <FormField
                  label="Receipt / Order #"
                  value={editReceiptNumber}
                  onChangeText={setEditReceiptNumber}
                  placeholder="e.g. TCG-8843, #001"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingDetails(false)}
                    className="flex-1 rounded-md bg-muted py-2 text-sm font-semibold text-muted-foreground hover:opacity-90"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveDetails}
                    className="flex-1 rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={openDetailsEditor}
                  className="group flex items-start gap-1.5 text-left"
                >
                  <span className="text-[22px] font-bold text-foreground">
                    {lot.seller || "Unknown Seller"}
                  </span>
                  <Pencil className="mt-2 h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                </button>
                {lot.purchaseLocation && (
                  <div className="mt-0.5 text-[13px] font-medium text-muted-foreground">
                    {lot.purchaseLocation}
                  </div>
                )}
                {lot.receiptNumber && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Receipt # {lot.receiptNumber}
                  </div>
                )}
              </>
            )}
            {lot.lotType && (
              <div className="pt-1">
                <Badge
                  label={lot.lotType}
                  variant={lotTypeBadge(lot.lotType)}
                />
              </div>
            )}
            <div className="text-[13px] text-muted-foreground">
              {lot.purchaseDate}
            </div>
          </div>
          <RequireRole min="manager">
            <div className="flex gap-2">
              {confirmDelete ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    className="rounded-md bg-muted px-3.5 py-1.5 text-[13px] font-semibold text-muted-foreground hover:opacity-90"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="rounded-md bg-destructive px-3.5 py-1.5 text-[13px] font-semibold text-destructive-foreground hover:opacity-90"
                  >
                    Delete
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-destructive hover:bg-muted/80"
                  aria-label="Delete lot"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </RequireRole>
        </div>

        <div className="space-y-2.5 rounded-md bg-primary p-4">
          <div className="flex justify-around">
            <CostItem
              value={`$${landed.toFixed(2)}`}
              label="Landed Cost"
              accent
            />
            <CostItem
              value={`$${costPerCard.toFixed(4)}`}
              label="Per Card"
              subLabel={
                showPortfolioAvg ? `avg $${portfolioAvg.toFixed(4)}` : undefined
              }
            />
            <CostItem
              value={lot.estimatedCards.toLocaleString()}
              label="Est. Cards"
            />
          </div>
          <div className="h-px bg-primary-foreground/20" />
          <div className="flex justify-around">
            <CostItem
              value={`$${lot.purchaseCost.toFixed(2)}`}
              label="Purchase"
              small
            />
            <CostItem
              value={`$${lot.shippingCost.toFixed(2)}`}
              label="Shipping"
              small
            />
            <CostItem
              value={`$${lot.taxFees.toFixed(2)}`}
              label="Tax/Fees"
              small
            />
          </div>
          {lotProjROI !== null && (
            <>
              <div className="h-px bg-primary-foreground/20" />
              <div className="flex justify-around">
                <CostItem
                  value={`${lotProjROI >= 0 ? "+" : ""}${lotProjROI.toFixed(1)}%`}
                  label="Proj. ROI"
                  accent
                />
                <CostItem
                  value={`$${lotInvValue.toFixed(2)}`}
                  label="Inv. Value"
                  small
                />
                <CostItem
                  value={`${lotProjProfit >= 0 ? "+" : ""}$${lotProjProfit.toFixed(2)}`}
                  label="Est. Profit"
                  small
                  profitTone={lotProjProfit >= 0 ? "good" : "bad"}
                />
              </div>
            </>
          )}
        </div>

        <div
          className={`space-y-2.5 rounded-md border bg-card p-3.5 ${
            isFullyInventoried
              ? "border-l-4 border-l-emerald-500 border-border"
              : "border-border"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Inventory Progress
            </span>
            {isFullyInventoried && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-emerald-700">
                <CheckCircle2 className="h-2.5 w-2.5" />
                Complete
              </span>
            )}
          </div>
          <div className="flex items-center">
            <ProgressStat
              value={totalInventoried.toLocaleString()}
              label="Inventoried"
              highlight={isFullyInventoried ? "complete" : "accent"}
            />
            <div className="mx-1 h-9 w-px bg-border" />
            <ProgressStat
              value={lot.estimatedCards.toLocaleString()}
              label="Est. Total"
            />
            <div className="mx-1 h-9 w-px bg-border" />
            <ProgressStat
              value={totalSorted.toLocaleString()}
              label="Bulk Sorted"
            />
          </div>
          {lot.estimatedCards > 0 && (
            <>
              <div className="h-2 overflow-hidden rounded bg-muted">
                <div
                  className={`h-full rounded transition-all ${
                    isFullyInventoried ? "bg-emerald-500" : "bg-secondary"
                  }`}
                  style={{
                    width: `${Math.min(100, (totalInventoried / lot.estimatedCards) * 100)}%`,
                  }}
                />
              </div>
              <div
                className={`text-center text-[11px] ${
                  isFullyInventoried
                    ? "text-emerald-600"
                    : "text-muted-foreground"
                }`}
              >
                {totalInventoried} of {lot.estimatedCards} cards inventoried
                {` · ${Math.round((totalInventoried / lot.estimatedCards) * 100)}%`}
              </div>
            </>
          )}
        </div>

        {rarityRows.length > 0 && (
          <div className="space-y-2.5 rounded-md border border-border bg-card p-3.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Rarity Breakdown
              </span>
              <span className="text-[10px] text-muted-foreground">
                {rarityCounts.total.toLocaleString()} tracked
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {rarityRows.map((r) => (
                <span
                  key={r}
                  className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-foreground"
                >
                  <span className="font-semibold">{r}</span>
                  <span className="text-muted-foreground">
                    ×{(rarityCounts.byRarity[r] ?? 0).toLocaleString()}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        <DropdownField
          label="TCG Game"
          value={lot.game ?? "Pokemon"}
          options={GAME_PICKER_OPTIONS.map((g) => ({
            value: g,
            label: GAME_LABELS[g],
          }))}
          onChange={handleGameChange}
        />

        <DropdownField
          label="Status"
          value={lot.status}
          options={LOT_STATUSES}
          onChange={handleStatusChange}
        />

        <SectionHeader
          title={`Sorting Bins (${totalSorted.toLocaleString()} sorted)`}
          secondaryAction={totalInventoried > 0 ? "From Inventory" : undefined}
          onSecondaryAction={
            totalInventoried > 0 ? () => setShowBackfillSheet(true) : undefined
          }
          action={expandBins ? "Collapse" : "Expand"}
          onAction={() => setExpandBins(!expandBins)}
        />

        <div className="space-y-2">
          {categoryIds.map((cat) => {
            const bin = lot.sortingBins.find((b) => b.category === cat);
            const isEditing = editingBin === cat;
            const Icon = iconForCategory(gameConfig.sortingCategories, cat);
            return (
              <div
                key={cat}
                ref={(el) => {
                  fieldRefs.current[cat] = el;
                }}
                className={`overflow-hidden rounded-md border bg-card ${
                  bin ? "border-secondary" : "border-border"
                }`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    isEditing ? setEditingBin(null) : openBinEditor(cat)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      isEditing ? setEditingBin(null) : openBinEditor(cat);
                    }
                  }}
                  className="flex w-full cursor-pointer items-center justify-between gap-2 p-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <div className="flex flex-1 items-center gap-2">
                    <Icon
                      className={`h-4 w-4 shrink-0 ${
                        bin
                          ? "text-secondary-foreground"
                          : "text-muted-foreground"
                      }`}
                    />
                    <span
                      className={`text-sm ${
                        bin
                          ? "font-semibold text-foreground"
                          : "text-muted-foreground"
                      }`}
                    >
                      {cat}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {bin ? (
                      <span className="text-base font-bold text-secondary-foreground">
                        {bin.count.toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        + Add
                      </span>
                    )}
                    {bin && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteBin(
                            confirmDeleteBin === cat ? null : cat,
                          );
                        }}
                        className="p-1 text-destructive hover:opacity-80"
                        aria-label="Remove bin"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {isEditing ? (
                      <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {confirmDeleteBin === cat && (
                  <div className="space-y-2 border-t border-border bg-muted px-3 py-2.5">
                    <div className="text-xs font-medium text-muted-foreground">
                      Remove this bin?
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteBin(null)}
                        className="rounded-md bg-card px-3.5 py-1.5 text-[13px] font-semibold text-muted-foreground hover:opacity-90"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteBin(cat)}
                        className="rounded-md bg-destructive px-3.5 py-1.5 text-[13px] font-semibold text-destructive-foreground hover:opacity-90"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {isEditing && confirmDeleteBin !== cat && (
                  <div className="space-y-1 border-t border-border p-3">
                    <NumberField
                      label="Count"
                      value={binCount}
                      onChangeText={(v) => {
                        setBinCount(v);
                        if (binCountError) setBinCountError(null);
                      }}
                      placeholder="0"
                      error={binCountError}
                    />
                    <FormField
                      label="Storage Location"
                      value={binLocation}
                      onChangeText={setBinLocation}
                      placeholder="BOX-A, BINDER-1, etc."
                    />
                    <FormField
                      label="Notes"
                      value={binNotes}
                      onChangeText={setBinNotes}
                      placeholder="Optional notes"
                    />
                    <PrimaryButton label="Save Bin" onClick={handleSaveBin} />
                  </div>
                )}

                {!isEditing && bin && expandBins && (
                  <div className="space-y-1 border-t border-border px-3 pb-2.5 pt-2">
                    {bin.location && (
                      <div className="text-xs text-muted-foreground">
                        Location: {bin.location}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      Cost Basis: ${(bin.count * costPerCard).toFixed(2)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {lot.notes && (
          <div className="space-y-1 rounded-md bg-muted p-3.5">
            <div className="text-[11px] font-medium tracking-wide text-muted-foreground">
              Notes
            </div>
            <p className="text-sm leading-5 text-foreground">{lot.notes}</p>
          </div>
        )}

        {confirmDelete ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="flex-1 rounded-md bg-muted py-2 text-center text-[13px] font-semibold text-muted-foreground hover:opacity-90"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="flex-1 rounded-md bg-destructive py-2 text-center text-[13px] font-semibold text-destructive-foreground hover:opacity-90"
            >
              Delete
            </button>
          </div>
        ) : (
          <PrimaryButton
            label="Delete Lot"
            onClick={handleDelete}
            variant="danger"
          />
        )}
      </div>
    </>
  );
}

function CostItem({
  value,
  label,
  subLabel,
  small,
  accent,
  profitTone,
}: {
  value: string;
  label: string;
  subLabel?: string;
  small?: boolean;
  accent?: boolean;
  profitTone?: "good" | "bad";
}) {
  const valColor = profitTone
    ? profitTone === "good"
      ? "text-emerald-300"
      : "text-destructive-foreground"
    : accent
      ? "text-emerald-200"
      : "text-primary-foreground";
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div
        className={`${small ? "text-[15px] font-semibold" : "text-xl font-bold"} ${valColor}`}
      >
        {value}
      </div>
      <div className="text-[10px] text-primary-foreground/60">{label}</div>
      {subLabel && (
        <div className="text-[10px] text-primary-foreground/40">{subLabel}</div>
      )}
    </div>
  );
}

function ProgressStat({
  value,
  label,
  highlight,
}: {
  value: string;
  label: string;
  highlight?: "complete" | "accent";
}) {
  const valColor =
    highlight === "complete"
      ? "text-emerald-600"
      : highlight === "accent"
        ? "text-secondary-foreground"
        : "text-foreground";
  return (
    <div className="flex flex-1 flex-col items-center gap-0.5">
      <div className={`text-lg font-bold ${valColor}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function BackfillSheet({
  rows,
  categories,
  onClose,
  onApply,
}: {
  rows: { cat: string; count: number }[];
  categories: SortingCategoryDef[];
  onClose: () => void;
  onApply: (mode: "merge" | "replace") => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end" role="dialog">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative w-full rounded-t-2xl border border-border bg-card pb-8 pt-3 shadow-2xl animate-in slide-in-from-bottom duration-200">
        <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-border" />
        <h3 className="px-5 text-[17px] font-bold text-foreground">
          Backfill from Inventory
        </h3>
        <p className="mb-4 px-5 text-[13px] text-muted-foreground">
          {rows.length > 0
            ? "Counts derived from cards inventoried to this lot."
            : "No inventory cards are linked to this lot yet."}
        </p>

        {rows.length > 0 && (
          <div className="mx-5 mb-4 overflow-hidden rounded-lg border border-border">
            {rows.map(({ cat, count }, i) => {
              const Icon = iconForCategory(categories, cat);
              return (
                <div
                  key={cat}
                  className={`flex items-center justify-between px-3.5 py-2.5 ${
                    i !== rows.length - 1 ? "border-b border-border" : ""
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="h-3.5 w-3.5 text-secondary-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      {cat}
                    </span>
                  </div>
                  <span className="text-[15px] font-bold text-secondary-foreground">
                    {count.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {rows.length > 0 && (
          <div className="mb-2 flex gap-2.5 px-5">
            <button
              type="button"
              onClick={() => onApply("merge")}
              className="flex flex-1 flex-col items-center gap-0.5 rounded-md bg-muted py-3.5"
            >
              <span className="text-[15px] font-bold text-foreground">
                Merge
              </span>
              <span className="text-[11px] text-muted-foreground">
                Fill zero-count bins only
              </span>
            </button>
            <button
              type="button"
              onClick={() => onApply("replace")}
              className="flex flex-1 flex-col items-center gap-0.5 rounded-md bg-primary py-3.5"
            >
              <span className="text-[15px] font-bold text-primary-foreground">
                Replace
              </span>
              <span className="text-[11px] text-primary-foreground/60">
                Overwrite all bin counts
              </span>
            </button>
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-2 w-full border-t border-border pt-3.5 text-center text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
